import React from "react";
import { Link } from "react-router-dom";
import {
  Ruler, Layers, Sun, Wind, FileCheck, ShieldCheck, Wallet, CloudLightning, Droplets,
  Check, ArrowRight, Phone, HelpCircle,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { PageHero } from "./Services";
import { brand, roofCost } from "../data/site";

// What actually moves the number on a Valley roof. No prices here by design —
// figures only appear once roofCost.showRanges is turned on in src/data/site.js.
const drivers = [
  {
    icon: Layers,
    title: "Which Roof System You Have",
    text:
      "Tile, shingle, foam and flat roofs are different jobs with different materials and labor. On a tile roof the tile often gets reused and only the underlayment is replaced, which lands nowhere near the price of a full tear-off.",
  },
  {
    icon: Ruler,
    title: "Square Footage & Pitch",
    text:
      "Roofing is priced by the square (100 square feet), not by the square footage of the house. A steep or cut-up roof with lots of valleys, hips and dormers takes longer per square than a simple low-slope roof.",
  },
  {
    icon: Wind,
    title: "What's Under There",
    text:
      "Rotted or delaminated decking, failed valley metal, rusted flashing and worn penetration boots are common and can't be seen from the driveway. An honest estimate says what's included and what happens if decking is found.",
  },
  {
    icon: Sun,
    title: "Rooftop Equipment & Penetrations",
    text:
      "Anything bolted to the roof has to come off and go back on. A rooftop package unit, skylights, a solar array or satellite mounts each add labor, and the mounts are where flashing has to be redone properly. We don't remove or reinstall solar arrays — that stays with your solar company — but we schedule around it.",
  },
  {
    icon: ShieldCheck,
    title: "Materials & Warranty Tier",
    text:
      "Underlayment weight, tile or shingle line, and the warranty tier a manufacturer will certify all change the price. This is the line item worth comparing closely between bids, because it's the one most often left vague.",
  },
  {
    icon: FileCheck,
    title: "Permits, HOA & Access",
    text:
      "City or county permits, HOA color and material approval, gated access, tight lots and tile that has to be carried rather than craned all take time, and time is most of the cost of a roof.",
  },
];

const questions = [
  "Is the underlayment brand, weight and line named, or just called \"new underlayment\"?",
  "Does the price include new valley metal, flashing and penetration boots, or are those extra?",
  "What happens if you find bad decking — is there a per-sheet price in writing?",
  "Is tile breakage and replacement included, and what happens if my profile is discontinued?",
  "If there is rooftop equipment or an array, who removes and reinstalls it, and who is responsible if something breaks?",
  "What's the material warranty, what's the workmanship warranty, and are both in writing?",
  "Is the contractor's ROC license number on the estimate, and is it active?",
  "Is this a full replacement, or would repairing a few slopes get me the same result?",
];

const RoofCost = () => {
  const showRanges = roofCost.showRanges && roofCost.ranges.length > 0;

  const faqs = [
    {
      q: "How much does a new roof cost in Phoenix?",
      a: "Enough depends on the specific roof that any number quoted before an inspection is a guess. The roof system, the size and pitch, the condition of the decking, and what has to be removed first all move the price substantially. Two homes on the same street can differ by thousands for reasons neither owner can see from the ground.",
    },
    {
      q: "Why is roof cost quoted per square instead of per square foot?",
      a: "A roofing square is 100 square feet of roof surface. Roofs are ordered, priced and installed in squares, and roof surface is always larger than the floor area of the house because of pitch and overhangs. A 2,000 square foot home does not have 2,000 square feet of roof.",
    },
    {
      q: "Is replacing tile underlayment cheaper than a new tile roof?",
      a: "Almost always, because the tile you already own gets lifted, stacked and reset rather than bought again. When the tile is sound, this is the more sensible spend and it resets the waterproofing life of the roof.",
    },
    {
      q: "Does insurance pay for a roof replacement in Arizona?",
      a: "Carriers cover sudden damage from a specific event — monsoon wind, hail, a fallen limb — not wear from age and sun. Whether a claim applies depends on your policy and what the inspection finds. We document damage with photos and a written scope your carrier can work from.",
    },
    {
      q: "Can I finance a roof replacement?",
      a: "Financing is a common way Valley homeowners handle a roof that failed before the savings were ready. Ask us what's currently available when we write the estimate.",
    },
    {
      q: "Is the cheapest roofing bid usually the problem?",
      a: "Not automatically, but a bid that is far below the others is usually missing something — thinner underlayment, flashing reused instead of replaced, no decking allowance, or a workmanship warranty that isn't in writing. Compare the line items, not the totals.",
    },
  ];

  return (
    <div>
      <SEO
        title="Roof Replacement Cost in Phoenix, AZ | What Drives Price"
        description="What drives roof replacement cost in Phoenix: roof system, square footage, pitch, decking, rooftop equipment and permits. Free inspection: (602) 834-4707."
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: "Roof Replacement Cost", item: `${SITE_URL}/roof-replacement-cost` },
              ],
            },
          ],
        }}
      />

      <PageHero
        eyebrow="Straight Answers"
        title="What A Roof Costs In Phoenix"
        subtitle="Why estimates differ by thousands, what belongs on a line-item bid, and how to tell a real number from a placeholder."
        image="/images/roofing-tile-hero.jpg"
        imageAlt="Tile roof on an Arizona home"
      />

      {/* Intro + form */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10 xl:gap-12">
          <div className="lg:col-span-3">
            <span className="kicker">The Honest Version</span>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-3 mb-4">
              Nobody Can Price Your Roof From The Street
            </h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground leading-relaxed text-lg mb-5">
              National cost calculators average a roof in Ohio with a roof in Arizona, and the result describes
              neither. The Valley has its own roof: concrete tile over felt that fails long before the tile does,
              foam roofs that live or die by recoating, and shingles cooking under sun that ages them faster than
              the brochure assumes.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-5">
              So here is what we tell people who call and ask for a number over the phone: we can tell you what
              drives the price, what should appear on any estimate you're handed, and what questions separate a
              real bid from a placeholder. Then we come out, get on the roof, and give you the actual figure in
              writing for free.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              One thing worth knowing before you budget for a replacement: on a tile roof, you may not need one.
              If the tile is sound, replacing the underlayment beneath it and resetting the same tile fixes the
              leak for a fraction of a tear-off.{" "}
              <Link to="/services/tile-roof-underlayment" className="text-brand-dark font-semibold hover:underline">
                See how underlayment replacement works
              </Link>
              .
            </p>

            {showRanges && (
              <div className="mt-10 bg-secondary border border-ink/10 rounded-2xl p-6 md:p-8">
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-1">Typical Ranges</h3>
                {roofCost.updated && (
                  <p className="text-muted-foreground text-xs uppercase tracking-widest mb-5">Updated {roofCost.updated}</p>
                )}
                <div className="space-y-3">
                  {roofCost.ranges.map((r) => (
                    <div key={r.system} className="flex flex-wrap items-baseline justify-between gap-2 bg-card border border-ink/10 rounded-xl px-5 py-4">
                      <div>
                        <div className="font-semibold">{r.system}</div>
                        {r.note && <div className="text-muted-foreground text-xs mt-0.5">{r.note}</div>}
                      </div>
                      {/* brand-dark, not brand: #FC3F00 is 3.58:1 on this
                          white card and fails AA. #BA4B00 is 5.1:1. */}
                      <div className="font-heading text-lg text-brand-dark">{r.range}</div>
                    </div>
                  ))}
                </div>
                <p className="text-muted-foreground text-xs mt-5 leading-relaxed">{roofCost.disclaimer}</p>
              </div>
            )}
          </div>

          <aside className="lg:col-span-2">
            <div className="lg:sticky lg:top-28">
              <div className="section-dark text-white rounded-2xl p-6 md:p-8">
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">Get The Real Number</h3>
                <p className="text-white/60 text-sm mb-6">
                  Free inspection, seven days a week, 7:00 AM to 7:00 PM — daylight hours, so the earliest and latest times shift through the year. Itemized estimate, no obligation.
                </p>
                <LeadForm dark defaultService="Roofing" />
              </div>
              <a href={`tel:${brand.phoneRaw}`} className="flex items-center justify-center gap-2 mt-4 border border-ink/15 hover:border-brand rounded-md py-4 font-semibold text-sm transition-colors">
                <Phone size={18} className="text-brand" /> {brand.phone}
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Cost drivers */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="kicker-dark">Where The Money Goes</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              Six Things That Move The Price
            </h2>
            <div className="brand-line mb-5" />
            <p className="text-white/70 text-lg">
              When two estimates for the same house are thousands apart, the difference is almost always hiding in
              one of these.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {drivers.map((d) => (
              <div key={d.title} className="card-lift bg-white/5 border border-white/10 rounded-2xl p-7">
                <d.icon size={30} className="text-brand mb-4" />
                <h3 className="font-heading text-xl uppercase tracking-wide mb-2">{d.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{d.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Questions checklist */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="kicker">Before You Sign</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              Eight Questions To Ask Any Roofer
            </h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground leading-relaxed">
              Ask every bidder the same eight questions and the cheap bid usually explains itself. Ask us too —
              our estimates name the manufacturer, line and color of every product going on your roof, including
              the upgrade we add at no charge.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors">
                Get a Free Estimate <ArrowRight size={18} />
              </Link>
              <Link to="/financing" className="inline-flex items-center gap-2 border border-ink/20 hover:border-brand hover:text-brand font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors">
                <Wallet size={16} /> Financing
              </Link>
            </div>
          </div>
          <ul className="space-y-3">
            {questions.map((q) => (
              <li key={q} className="flex gap-3 bg-secondary rounded-xl px-5 py-4">
                <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={14} className="text-brand" />
                </span>
                <span className="text-sm leading-relaxed">{q}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Repair vs replace + storm */}
      <section className="py-20 bg-secondary border-y border-ink/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-6">
          <Link to="/services/tile-roof-underlayment" className="card-lift bg-card border border-ink/10 rounded-2xl p-7 block">
            <Layers size={28} className="text-brand mb-4" />
            <h3 className="font-heading text-xl uppercase tracking-wide mb-2">Maybe It's The Underlayment</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              The most common tile roof fix in the Valley, and far cheaper than a replacement.
            </p>
          </Link>
          <Link to="/insurance-claims" className="card-lift bg-card border border-ink/10 rounded-2xl p-7 block">
            <CloudLightning size={28} className="text-brand mb-4" />
            <h3 className="font-heading text-xl uppercase tracking-wide mb-2">Storm Damage & Claims</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              If monsoon damage caused it, the cost conversation starts with your carrier, not your savings.
            </p>
          </Link>
          <Link to="/services/foam-roof-coating" className="card-lift bg-card border border-ink/10 rounded-2xl p-7 block">
            <Droplets size={28} className="text-brand mb-4" />
            <h3 className="font-heading text-xl uppercase tracking-wide mb-2">Foam & Flat Roofs</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Recoating on schedule costs a fraction of letting a flat roof go and replacing the system.
            </p>
          </Link>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-background">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <span className="kicker">Answers</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Roof Cost FAQs</h2>
            <div className="brand-line mx-auto" />
          </div>
          <div className="space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="bg-card border border-ink/10 rounded-xl p-6">
                <h3 className="font-heading text-lg uppercase tracking-wide mb-2 flex items-start gap-2">
                  <HelpCircle size={18} className="text-brand shrink-0 mt-1" /> {f.q}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoofCost;
